/**
 * Centralized icon module for the desktop app.
 *
 * Wraps Solar Icons (`@solar-icons/react`) with `weight="Linear"` defaulted
 * so callers don't have to think about it, and re-exports each icon under
 * the same name the codebase already used for the Phosphor equivalent it
 * replaces. Migrating a file is therefore just an import-path change:
 *
 *   - import { Hash } from "@/components/ui/icons";
 *   + import { Hash } from "@/components/ui/icons";
 *
 * NOTE: Phosphor weights are lowercase ("bold", "fill"); Solar weights are
 * PascalCase ("Bold", "Linear", "Outline", "BoldDuotone", "LineDuotone",
 * "Broken"). Migration sweeps PascalCase the call-site weights.
 *
 * Phosphor's `fill` weight has no perfect Solar analog — the closest visual
 * is `Bold` (filled-look heavy stroke). Migration maps fill→Bold.
 *
 * Imperfect mappings (Solar has no exact equivalent — closest visual chosen):
 *
 *   Plus            → AddSquare        (no plain `+`)
 *   Plug            → PlugCircle
 *   PlugsConnected  → LinkRound
 *   Diamond         → Box
 *   Circle          → Round
 *   Square          → Box
 *   Star            → Round
 *   Broadcast       → WiFiRouter
 *   Funnel          → Filter
 *   Megaphone       → Speaker
 *   Quotes          → ChatRoundLine
 *   TextStrikethrough → TextCross (Solar's text-with-cross-line)
 *   FilePdf         → DocumentText
 *   Cube / Square   → Box
 *   Brain / Robot   → Cpu
 *   ArrowsIn / ArrowsOut → Maximize
 *   ArrowSquareOut  → ArrowRightUp
 *   ArrowBendUpLeft → UndoLeft
 *   X               → CloseSquare
 *   ClockCounterClockwise → ClockCircle
 *   IdentificationBadge → PassportControl
 */
import type { ComponentType } from "react";
import {
  AddCircle,
  AddSquare,
  AltArrowDown,
  AltArrowLeft,
  AltArrowRight,
  AltArrowUp,
  Archive as SolarArchive,
  ArrowDown as SolarArrowDown,
  ArrowLeft as SolarArrowLeft,
  ArrowRight as SolarArrowRight,
  ArrowRightUp,
  ArrowToTopLeft,
  ArrowToTopRight,
  ArrowUp as SolarArrowUp,
  Bell as SolarBell,
  BellOff,
  Bolt,
  Box,
  Buildings,
  Calendar as SolarCalendar,
  CalendarAdd,
  ChatRound,
  ChatRoundLine,
  CheckCircle as SolarCheckCircle,
  CheckRead,
  Checklist,
  CheckSquare as SolarCheckSquare,
  ClockCircle,
  CloseSquare,
  Cloud,
  Code as SolarCode,
  CodeSquare,
  Command as SolarCommand,
  Copy as SolarCopy,
  Cpu as SolarCpu,
  Crown as SolarCrown,
  DangerCircle,
  DangerTriangle,
  Document,
  DocumentText,
  Download,
  EyeClosed,
  Eye as SolarEye,
  Filter,
  Fire as SolarFire,
  Flag as SolarFlag,
  Forward,
  Gallery,
  Global,
  HamburgerMenu,
  Hashtag,
  HeadphonesRound as SolarHeadphones,
  Heart as SolarHeart,
  Inbox,
  Letter,
  LinkBroken,
  Link as SolarLink,
  LinkRound,
  List,
  Lock as SolarLock,
  LockUnlocked,
  Logout,
  MagicStick,
  Magnifer,
  FullScreen,
  MaximizeSquareMinimalistic,
  MentionCircle,
  MenuDots,
  MinusCircle,
  MonitorSmartphone as SolarMonitorSmartphone,
  MusicNote as SolarMusicNote,
  NotebookBookmark,
  Paperclip as SolarPaperclip,
  Passport,
  Pause as SolarPause,
  Pen,
  Phone as SolarPhone,
  Pin,
  Pip as SolarPip,
  Plain,
  Play as SolarPlay,
  PlugCircle,
  QuitFullScreen,
  QuitPip,
  Refresh,
  RefreshSquare,
  Rocket as SolarRocket,
  Settings,
  Share,
  Shield as SolarShield,
  ShieldCheck as SolarShieldCheck,
  Shuffle as SolarShuffle,
  SmileCircle,
  Sort,
  Speaker,
  Stop as SolarStop,
  Suitcase,
  Tag as SolarTag,
  Target as SolarTarget,
  Text,
  TextBold,
  TextCross,
  TextItalic as SolarTextItalic,
  Ticket as SolarTicket,
  TrashBinTrash,
  UndoLeft,
  User as SolarUser,
  UserCircle as SolarUserCircle,
  UserPlus as SolarUserPlus,
  UsersGroupRounded,
  UsersGroupTwoRounded,
  VerifiedCheck,
  Videocamera,
  VolumeCross,
  VolumeLoud,
  Wallpaper,
  WiFiRouter,
  type IconProps,
} from "@solar-icons/react";

/** The component type for any icon exported from this module. Used by
 * shared abstractions (PageHeader, NavItem, list-templates) that accept
 * an icon-as-prop. Aliases Solar's `ComponentType<IconProps>`. */
export type Icon = ComponentType<IconProps>;

const linear =
  (Inner: ComponentType<IconProps>): Icon =>
  (props) => <Inner weight="Linear" {...props} />;

// Sidebar / nav ─────────────────────────────────────────────────────────
export const Hash = linear(Hashtag);
export const HashStraight = linear(Hashtag);
export const Plus = linear(AddSquare);
export const PlusCircle = linear(AddCircle);
export const CaretRight = linear(AltArrowRight);
export const CaretLeft = linear(AltArrowLeft);
export const CaretDown = linear(AltArrowDown);
export const CaretUp = linear(AltArrowUp);
export const CaretCircleDown = linear(AltArrowDown);
export const GearSix = linear(Settings);
export const Gear = linear(Settings);
export const Archive = linear(SolarArchive);
export const UsersThree = linear(UsersGroupRounded);
export const Users = linear(UsersGroupRounded);
export const ListBullets = linear(List);
// Solar List is horizontal-lines only — fine for Tables nav, wrong for
// composer bullet lists. Lucide List / ListOrdered pair as the canonical
// formatting-toolbar marks (matches at 16px).
export { List as BulletList, ListOrdered as ListNumbers } from "lucide-react";
export const GlobeSimple = linear(Global);
export const Globe = linear(Global);
export const GlobeHemisphereWest = linear(Global);
export const Tray = linear(Inbox);
export const PlugsConnected = linear(LinkRound);
export const Plugs = linear(PlugCircle);
export const UserPlus = linear(SolarUserPlus);
// A monitor + phone side by side — "get the apps" (desktop + iOS).
export const MonitorSmartphone = linear(SolarMonitorSmartphone);

// User profile ──────────────────────────────────────────────────────────
export const SignOut = linear(Logout);
export const PencilSimple = linear(Pen);
export const PencilLine = linear(Pen);
export const User = linear(SolarUser);
export const UserCircle = linear(SolarUserCircle);
export const UserCircleDashed = linear(SolarUserCircle);
export const Bell = linear(SolarBell);
export const BellSlash = linear(BellOff);
export const Plug = linear(PlugCircle);
export const DownloadSimple = linear(Download);
export const MagnifyingGlass = linear(Magnifer);
export const At = linear(MentionCircle);
export const IdentificationBadge = linear(Passport);

// Arrows / chevrons ─────────────────────────────────────────────────────
export const ArrowDown = linear(SolarArrowDown);
export const ArrowLeft = linear(SolarArrowLeft);
export const ArrowRight = linear(SolarArrowRight);
export const ArrowUp = linear(SolarArrowUp);
export const ArrowLineLeft = linear(ArrowToTopLeft);
export const ArrowLineRight = linear(ArrowToTopRight);
export const ArrowSquareOut = linear(ArrowRightUp);
export const ArrowBendUpLeft = linear(UndoLeft);
export const ArrowsClockwise = linear(Refresh);
export const ArrowClockwise = linear(Refresh);
export const ArrowCounterClockwise = linear(RefreshSquare);
export const ArrowsIn = linear(MagicStick);
export const ArrowsInSimple = linear(MagicStick);
export const ArrowsOut = linear(MagicStick);
export const EnterFullScreen = linear(FullScreen);
export const ExitFullScreen = linear(QuitFullScreen);
export const PictureInPicture = linear(SolarPip);
export const QuitPictureInPicture = linear(QuitPip);
export const ResizeHandle = linear(MaximizeSquareMinimalistic);

// Communication / chat / mail ───────────────────────────────────────────
export const ChatCircle = linear(ChatRound);
export const ChatText = linear(ChatRound);
export const Envelope = linear(Letter);
export const EnvelopeSimple = linear(Letter);
export const PaperPlaneRight = linear(Plain);
export const PaperPlaneTilt = linear(Plain);
export const Megaphone = linear(Speaker);
export const Quotes = linear(ChatRoundLine);
export const Phone = linear(SolarPhone);
export const PhoneDisconnect = linear(SolarPhone);
export const Headphones = linear(SolarHeadphones);
export const VideoCamera = linear(Videocamera);
export const Broadcast = linear(WiFiRouter);

// Files / docs / media ──────────────────────────────────────────────────
export const File = linear(Document);
export const FileText = linear(Document);
export const FilePdf = linear(DocumentText);
export const FileLock = linear(Document);
export const FileXls = linear(Document);
export const FileZip = linear(Document);
export const Paperclip = linear(SolarPaperclip);
export const Image = linear(Gallery);
export const Gif = linear(Gallery);
export const MusicNote = linear(SolarMusicNote);

// Playback / volume ─────────────────────────────────────────────────────
export const Play = linear(SolarPlay);
export const Pause = linear(SolarPause);
export const Stop = linear(SolarStop);
export const Shuffle = linear(SolarShuffle);
export const FastForward = linear(Forward);
export const SpeakerSimpleHigh = linear(VolumeLoud);
export const SpeakerSimpleSlash = linear(VolumeCross);

// Calendar / time ───────────────────────────────────────────────────────
export const Calendar = linear(SolarCalendar);
export const CalendarBlank = linear(SolarCalendar);
export const CalendarPlus = linear(CalendarAdd);
export const Clock = linear(ClockCircle);
export const ClockCounterClockwise = linear(ClockCircle);

// Code / dev ────────────────────────────────────────────────────────────
export const Code = linear(SolarCode);
export const CodeBlockIcon = linear(CodeSquare);
export const Command = linear(SolarCommand);
export const Copy = linear(SolarCopy);
export const CopySimple = linear(SolarCopy);
export const Cpu = linear(SolarCpu);
export const GitBranch = linear(CodeSquare);
export const Terminal = linear(SolarCode);
export const TerminalWindow = linear(SolarCode);
export const Robot = linear(SolarCpu);
export const Brain = linear(SolarCpu);

// Misc objects / shapes ─────────────────────────────────────────────────
export const Crown = linear(SolarCrown);
/** Verified-team badge — used next to @ano.dev users in MessageHeader.
 *  Solar's purpose-built verified shape (starburst). Stays in `text-primary`
 *  brand green at the call site. Bold weight by default for visual punch. */
export const Verified = (props: IconProps) => (
  <VerifiedCheck weight="Bold" {...props} />
);
export const Cube = linear(Box);
export const Diamond = linear(Box);
export const Circle = linear(MinusCircle);
export const CircleHalf = linear(MinusCircle);
export const Square = linear(Box);
export const Dot = linear(Pin);
export const Minus = linear(MinusCircle);
export const Star = linear(Pin);
export const Heart = linear(SolarHeart);
export const Fire = linear(SolarFire);
export const Flag = linear(SolarFlag);
export const Tag = linear(SolarTag);
export const Target = linear(SolarTarget);
export const PushPin = linear(Pin);
export const AddressBook = linear(NotebookBookmark);
export const Briefcase = linear(Suitcase);
export const Background = linear(Wallpaper);
export const BuildingApartment = linear(Buildings);
export const Rocket = linear(SolarRocket);
export const Ticket = linear(SolarTicket);
export const Wrench = linear(Settings);
export const Smiley = linear(SmileCircle);

// Status / safety ───────────────────────────────────────────────────────
export const Check = linear(CheckRead);
export const CheckCircle = linear(SolarCheckCircle);
export const CheckFat = linear(SolarCheckCircle);
export const CheckSquare = linear(SolarCheckSquare);
export const CheckSquareOffset = linear(Checklist);
export const Warning = linear(DangerTriangle);
export const WarningCircle = linear(DangerCircle);
export const Prohibit = linear(DangerCircle);
export const Lightning = linear(Bolt);
export const MagicWand = linear(MagicStick);
export const Shield = linear(SolarShield);
export const ShieldCheck = linear(SolarShieldCheck);
export const Lock = linear(SolarLock);
export const LockSimple = linear(SolarLock);
export const Eye = linear(SolarEye);
export const EyeSlash = linear(EyeClosed);
export const Trash = linear(TrashBinTrash);
export const LockKeyOpen = linear(LockUnlocked);

// Lists / sort / drag ───────────────────────────────────────────────────
export const Funnel = linear(Filter);
export const Rows = linear(HamburgerMenu);
export const Table = linear(HamburgerMenu);
export const SortAscending = linear(Sort);
export const SortDescending = linear(Sort);
export const DotsThree = linear(MenuDots);
export const DotsThreeVertical = linear(MenuDots);
export const DotsSixVertical = linear(MenuDots);

// Text formatting ───────────────────────────────────────────────────────
export const TextB = linear(TextBold);
export const TextItalic = linear(SolarTextItalic);
export const TextStrikethrough = linear(TextCross);
export const TextAa = linear(Text);
export const TextT = linear(Text);
// Lucide TextQuote — Solar has no quotation-marks glyph; ChatRoundLine stays
// on `Quotes` for thread actions (MessageActions, MessageContextMenu).
export { TextQuote as BlockquoteIcon } from "lucide-react";

// Misc ──────────────────────────────────────────────────────────────────
export const X = linear(CloseSquare);
export { Cloud };
// Spaces / external-collab channel mark — Solar's two-stacked-groups
// reads as "two parties" more clearly than HandShake at small sizes.
export const Handshake = linear(UsersGroupTwoRounded);
export const Handle = linear(Pin);
export const ShareNetwork = linear(Share);
export const Link = linear(SolarLink);
export const LinkSimple = linear(SolarLink);
export const LinkBreak = linear(LinkBroken);
export const House = linear(Buildings);
export const Component = linear(Box);
